import { useEffect, useState } from "react"
import UserRedemptionsList from "~components/users/UserRedemptionsList"
import useUserRedemptions from "~hooks/useUserRedemptions"
import { useLocation, useParams } from "react-router-dom"
import { Card } from "~components/Card"
import { ButtonPrimarySoftLarge } from "~styled/button"
import PerksModal from "~components/users/PerksModal"
import usePerks from "~hooks/usePerks"
import { GetCompanyOptionsShape } from "~shapes/company"
import { STATUS } from "~constants/status"
import { TableTopGroup } from "~styled/table"
import { PerkShape } from "~shapes/perk"
import RedeemModal from "~components/redemption/RedeemModal"
import { toast } from "react-toastify"
import PATHS, { getFullPath } from "~constants/paths"
import BackLink from "~components/BackLink"
import { ContainerSpaceBetween } from "~styled/containers"

export interface RedeemProps {
  perkId: string
  payload: Object | ""
}

const Redemptions = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const state = location.state as { email?: string } | null
  const userEmail = state?.email || ""

  const {
    data: redemptions,
    refetch: fetchRedemptions,
    isFetching,
    isError: errorFetchingRedemptions,
  } = useUserRedemptions(id || "")
  const [isPerksModalOpened, setIsPerksModalOpened] = useState<boolean>(false)
  const [isRedeemModalOpened, setIsRedeemModalOpened] = useState<boolean>(false)
  const [selectedPerk, setSelectedPerk] = useState<PerkShape | undefined>()

  const [options, setOptions] = useState<GetCompanyOptionsShape | undefined>(
    undefined
  )
  const tableOptions = { sort: "name", size: 40, status: STATUS.ACTIVE }

  const {
    data: perksData,
    refetch: fetchPerks,
    isFetching: isLoadingPerks,
  } = usePerks({ ...tableOptions, ...options })

  useEffect(() => {
    if (errorFetchingRedemptions) {
      toast.error(`Failed to load user redemptions`)
    }
  }, [errorFetchingRedemptions])

  useEffect(() => {
    if (!id) return
    ;(async () => {
      await fetchRedemptions()
    })()
  }, [id])

  const onOpenModal = async () => {
    setIsPerksModalOpened(true)
    await fetchPerks()
  }

  const onCloseModal = async () => {
    setIsPerksModalOpened(false)
  }

  useEffect(() => {
    if (isPerksModalOpened) {
      ;(async () => {
        await fetchPerks()
      })()
    }
  }, [options])

  const onSelectPerk = async (perk: PerkShape) => {
    setSelectedPerk(perk)
    if (
      confirm(
        `Are you absolutely certain you wish to redeem this perk on behalf of the user ${userEmail}?\n` +
          "\n" +
          "Proceeding with this action will finalize the redemption process and cannot be reverted. Please ensure that you have received confirmation from the user before proceeding."
      )
    ) {
      setIsPerksModalOpened(false)
      setIsRedeemModalOpened(true)
    }
  }

  const onRedeemModalClose = async (redeemed: boolean) => {
    setIsRedeemModalOpened(false)
    if (redeemed) await fetchRedemptions()
  }

  return (
    <>
      <BackLink text="Back to Users" path={`${getFullPath(PATHS.users)}`} />
      <Card>
        <PerksModal
          isLoading={isLoadingPerks}
          isOpened={isPerksModalOpened}
          perks={perksData?.result}
          onRequestClose={onCloseModal}
          onRequestConfirm={onCloseModal}
          search={el => setOptions(el)}
          onSelectPerk={onSelectPerk}
        />

        {isRedeemModalOpened && (
          <RedeemModal
            closeModal={onRedeemModalClose}
            isOpened={isRedeemModalOpened}
            perk={selectedPerk}
            userId={id}
          />
        )}
        <ContainerSpaceBetween style={{ marginBottom: 30 }}>
          <span style={{ fontSize: 15, fontWeight: "bold" }}>
            User: {userEmail}
          </span>

          <ButtonPrimarySoftLarge onClick={onOpenModal}>
            Redeem perk
          </ButtonPrimarySoftLarge>
        </ContainerSpaceBetween>

        <UserRedemptionsList redemptions={redemptions} isLoading={isFetching} />
      </Card>
    </>
  )
}

export default Redemptions
